import axios, { isAxiosError, type InternalAxiosRequestConfig } from 'axios'
import { extractApiError } from '@/lib/errors'
import { endSession, getAccessToken, getCompanyId } from '@/lib/session'

export const COMPANY_HINT_HEADER = 'x-company-id'

export const NO_COMPANY = '@Habitar:no-company'

const SESSION_ENDED_CODES = ['AUTH_TOKEN_INVALID', 'AUTH_TOKEN_MISSING']

const ApiInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 60000
})

ApiInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (!config.headers.has('Authorization')) {
    const token = getAccessToken()
    if (token) config.headers.set('Authorization', `Bearer ${token}`)
  }

  const companyHint = config.headers.get(COMPANY_HINT_HEADER)
  if (companyHint === NO_COMPANY) config.headers.delete(COMPANY_HINT_HEADER)
  else if (!companyHint) {
    const companyId = getCompanyId()
    if (companyId) config.headers.set(COMPANY_HINT_HEADER, companyId)
    else config.headers.delete(COMPANY_HINT_HEADER)
  }

  return config
})

async function decodeBlobErrorBody(error: unknown) {
  const response = isAxiosError(error) ? error.response : undefined
  if (!(response?.data instanceof Blob) || !response.data.type.includes('json')) return
  try {
    response.data = JSON.parse(await response.data.text())
  } catch {
    return
  }
}

ApiInstance.interceptors.response.use(
  response => response,
  async error => {
    await decodeBlobErrorBody(error)
    if (SESSION_ENDED_CODES.includes(extractApiError(error).code)) endSession()
    return Promise.reject(error)
  }
)

export const scopedTo = (companyId?: string | null) => (companyId ? { headers: { [COMPANY_HINT_HEADER]: companyId } } : {})

export const unscoped = () => ({ headers: { [COMPANY_HINT_HEADER]: NO_COMPANY } })

export { ApiInstance }
